<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  PopoverArrow,
  PopoverClose,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { v4 as uuid } from "uuid";
import { computed, inject } from "vue";
import WaveSurfer from "wavesurfer.js";
import { useRouter } from "vue-router";
import { watch, useTemplateRef, ref } from "vue";
import Zoom from "wavesurfer.js/dist/plugins/zoom.esm.js";
import Regions, { type Region } from "wavesurfer.js/dist/plugins/regions.esm.js";

import { useAnnotationStore, useMessageStore } from "@/stores";

import { deleteAnnotation, saveAnnotation } from "@/services/AnnotationService";
import { isTimeIndexRedaction, toDuration, toDurationHumanReadableText } from "@/services/UtilitiesService";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { TimeIndexRedaction } from "@/models/Annotation";

const MAX_PX_PER_SEC = 75;
const DEFAULT_REDACTION_LENGTH = 60;
const MAX_DURATION_RATIO_ALLOWED = 75;

const open = defineModel<boolean>("open", { required: true });
withDefaults(defineProps<{
  startTime: number;
  annotation: TimeIndexRedaction | undefined;
  fileReference: string;
  audio: { url: string; peaks: number[] };
  duration: number;
  playbackSpeedSteps: number[];
}>(), {  })

const CSP_NONCE = inject<string>("NONCE");

const $router = useRouter();
const routeParams = useCaseRouteParams();

const playerRef = useTemplateRef<HTMLElement | null>("container");
const mediaRef = useTemplateRef<HTMLMediaElement | null>("mediaRef");

const isSaving = ref(false);
const isDeleting = ref(false);
const simulateRedaction = ref(false);

const regionEnd = ref(0);
const regionStart = ref(0);

const wsVolume = ref(100);
const wsPxPerSec = ref(0);
const isPlaying = ref(false);
const wsPlaybackSpeed = ref(1);

const MIN_PX_PER_SEC = ref(0 /* For no-min */);

const validationMessage = computed(() => {
  if (regionEnd.value - regionStart.value < 1) {
    return "A minimum of 1 second of audio must be selected";
  }

  const ratioSelected = Math.round((100 * (regionEnd.value - regionStart.value)) / __props.duration);
  if (ratioSelected > MAX_DURATION_RATIO_ALLOWED) {
    return `You have selected ${ratioSelected}% of audio. The maximum allowed is ${MAX_DURATION_RATIO_ALLOWED}%.`;
  }

  return "";
});

const region = ref<Region>();
const surfer = ref<WaveSurfer>();

const markMetadata = computed(() => {
  return isTimeIndexRedaction(__props.annotation)
    ? __props.annotation
    : {
        uuid: undefined,
        startTime: __props.startTime,
        endTime: Math.min(__props.duration, __props.startTime + DEFAULT_REDACTION_LENGTH),
      };
});

const calculatedZoom = computed(() =>
  MIN_PX_PER_SEC.value === MAX_PX_PER_SEC
    ? 0
    : Math.round(((wsPxPerSec.value - MIN_PX_PER_SEC.value) / (MAX_PX_PER_SEC - MIN_PX_PER_SEC.value)) * 100),
);

watch(
  playerRef,
  (_, _a, cleanup) => {
    if (playerRef.value == null || mediaRef.value == null) return;

    regionStart.value = markMetadata.value.startTime;
    regionEnd.value = markMetadata.value.endTime;

    const regions = Regions.create();
    const ws = WaveSurfer.create({
      container: playerRef.value,
      media: mediaRef.value,
      height: "auto", // Must be specified
      waveColor: "#636463",
      progressColor: "#636463",
      backend: "MediaElement",
      minPxPerSec: MIN_PX_PER_SEC.value,
      plugins: [regions, Zoom.create({ maxZoom: MAX_PX_PER_SEC })],
      autoScroll: false,
      autoCenter: false,
      cspNonce: CSP_NONCE,
    });
    surfer.value = ws;

    ws.on("zoom", savePxPerSec);
    ws.on("play", () => {
      isPlaying.value = true;
      scrollToCenter(ws.getCurrentTime());
    });
    ws.on("pause", () => {
      isPlaying.value = false;
      scrollToCenter(ws.getCurrentTime());
    });
    ws.on("audioprocess", (currentTime) => {
      if (simulateRedaction.value && currentTime >= regionStart.value && currentTime <= regionEnd.value)
        ws.setVolume(0);
      else ws.setVolume(wsVolume.value / 100);
    });
    ws.on("resize", () => {
      // Get zoom before recalc
      const currentZoom = calculatedZoom.value;

      // Calculate the actual minimum based on container width and audio duration
      MIN_PX_PER_SEC.value = ws.getWidth() / ws.getDuration();

      // Update current zoom to preserve zoom level while respecting min
      savePxPerSec(Math.max(MIN_PX_PER_SEC.value, (currentZoom * MAX_PX_PER_SEC) / 100));
    });
    ws.once("decode", () => {
      if (ws == null) return;

      ws.setTime(regionStart.value);

      const wsRegion = regions.addRegion({
        start: regionStart.value,
        end: regionEnd.value,
        resize: true,
        drag: true,
        color: "#CED3DC80" /* French Gray with 50% opacity */,
      });

      wsRegion.on("update-end", () => {
        if (wsRegion == null) return;
        // Update range
        regionEnd.value = wsRegion.end;
        regionStart.value = wsRegion.start;

        // Pause surfer
        ws.pause();
      });
      region.value = wsRegion;
    });

    cleanup(() => ws.destroy());
  },
  { flush: "post" },
);

watch([simulateRedaction, regionStart, regionEnd], () => {
  if (surfer.value == null) return;

  let resultingPeaks = __props.audio.peaks;

  if (simulateRedaction.value) {
    const startIndex = Math.floor(resultingPeaks.length * (regionStart.value / __props.duration));
    const endIndex = Math.floor(resultingPeaks.length * (regionEnd.value / __props.duration));
    resultingPeaks = resultingPeaks.map((_, idx) => (idx >= startIndex && idx <= endIndex ? 0 : _));
  }

  surfer.value.load(__props.audio.url, [resultingPeaks], __props.duration);
});

function savePxPerSec(pxPerSec: number) {
  wsPxPerSec.value = pxPerSec;
}

function setVolume(value: number) {
  wsVolume.value = value;
}

function setPlaybackSpeed(value: number) {
  wsPlaybackSpeed.value = value;
  surfer.value?.setPlaybackRate(value);
}

function scrollToCenter(time: number) {
  if (playerRef.value == null || surfer.value == null) return;

  const width = playerRef.value.getBoundingClientRect().width;
  surfer.value.setScrollTime(time - width / (wsPxPerSec.value * 2));
}

async function saveTimeIndexRedaction() {
  if (validationMessage.value !== "") return;

  const parsedMarkData: TimeIndexRedaction = {
    type: "TimeIndexRedaction",
    startTime: regionStart.value,
    endTime: regionEnd.value,
    uuid: markMetadata.value.uuid ?? uuid(),
  };

  try {
    isSaving.value = true;

    await saveAnnotation({
      evidenceThemes: undefined,
      fileReference: __props.fileReference,
      markId: parsedMarkData.uuid,
      source: "user",
      metadata: undefined,
      supportingFileEntryFileReference: "",
      parsedMarkData,
      markData: JSON.stringify(parsedMarkData),
    });

    $router.push({
      name: "casefileannotation",
      params: {
        brand: routeParams.value.brand,
        casename: routeParams.value.casename,
        casefile: __props.fileReference,
        annotation: parsedMarkData.uuid,
      },
    });

    // Close Dialog
    open.value = false;
  } catch (e) {
    console.error(e);

    useMessageStore().updateMessage({
      type: "error",
      title: "Saving Audio Redaction",
      message:
        "An error occured while saving this audio redaction. Please try again and report this is issue if it persists.",
      autoHide: true,
      showReload: false,
    });
  } finally {
    isSaving.value = false;
  }
}

async function onDeleteAnnotation() {
  isDeleting.value = false;

  if (markMetadata.value.uuid == null) return;

  const mark = useAnnotationStore().annotations.find((mark) => mark.markId === markMetadata.value.uuid);
  if (mark == null) return;

  try {
    isDeleting.value = true;
    await deleteAnnotation(mark);
    open.value = false;

    if (routeParams.value.annotation === markMetadata.value.uuid) {
      $router.push({
        name: "casefile",
        params: {
          brand: routeParams.value.brand,
          casename: routeParams.value.casename,
          casefile: __props.fileReference,
        },
      });
    }
  } catch (e) {
    console.error(e);

    useMessageStore().updateMessage({
      type: "error",
      title: "Deleting Audio Redaction",
      message:
        "An error occured while deleting this audio redaction. Please try again and report this is issue if it persists.",
      autoHide: true,
      showReload: false,
    });

    return;
  } finally {
    isDeleting.value = false;
  }
}
</script>
<template>
  <DialogRoot v-model:open="open">
    <DialogPortal>
      <DialogOverlay class="tw-fixed tw-inset-0 tw-z-[1000000] tw-h-full tw-w-full tw-bg-black/30" />
      <DialogContent
        :aria-describedby="undefined"
        class="tw-fixed tw-inset-0 tw-z-[1000000] tw-flex tw-items-center tw-justify-center *:tw-pointer-events-auto"
        style="pointer-events: none"
      >
        <div
          class="tw-grid tw-max-h-[90vh] tw-w-full tw-max-w-[90vw] tw-grid-rows-[min-content_minmax(10rem,1fr)_min-content] tw-gap-2 tw-overflow-y-auto tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-4 tw-shadow-lg [grid-template-areas:'title'_'container'_'controls'_'content'] *:tw-min-h-0"
        >
          <div class="tw-flex tw-items-center tw-justify-between [grid-area:title]">
            <DialogTitle class="tw-text-base tw-font-medium"> Redact Audio </DialogTitle>
            <DialogClose class="tw-grid tw-size-6 tw-place-items-center tw-rounded-full tw-p-1 hover:tw-bg-black/20">
              <i class="fas fa-xmark"></i>
            </DialogClose>
          </div>
          <div
            ref="container"
            class="tw-relative tw-h-full tw-max-h-60 tw-overflow-hidden [grid-area:container] *:tw-h-full *:tw-rounded-sm *:tw-ring-1 *:tw-ring-inset *:tw-ring-[#636463]/50"
          />
          <!-- Our method of loading the audio through wavesurfer doesn't allow setting the crossorigin attribute. So we must set the external media element -->
          <audio ref="mediaRef" crossorigin="use-credentials" class="tw-sr-only" />
          <div
            class="tw-grid tw-grid-cols-[minmax(0,1fr)_max-content_minmax(0,1fr)] tw-gap-1 tw-px-2 tw-text-xl [grid-area:controls] [&>*]:tw-min-w-0"
          >
            <div>
              <span class="tw-text-sm tw-text-red-700">{{ validationMessage }}</span>
            </div>
            <div class="tw-flex tw-gap-2">
              <button class="tw-text-purple-branddark" @click="() => (isPlaying ? surfer?.pause() : surfer?.play())">
                <i v-if="isPlaying" class="far fa-pause-circle tw-text-3xl" />
                <i v-else class="far fa-play-circle tw-text-3xl" />
              </button>
            </div>
            <div class="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <DropdownMenuRoot>
                <DropdownMenuTrigger
                  class="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-rounded-md tw-bg-gray-200 tw-px-4 tw-py-2 tw-text-sm tw-outline-none hover:tw-bg-gray-400"
                >
                  Volume ({{ wsVolume }}%)
                  <i aria-hidden="true" class="fas fa-chevron-right"></i>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  :side-offset="10"
                  class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
                >
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    class="tw-w-40"
                    aria-label="Volume Slider"
                    :value="wsVolume"
                    @change="(event) => setVolume((event.target as HTMLInputElement | null)?.valueAsNumber ?? 100)"
                  />
                </DropdownMenuContent>
              </DropdownMenuRoot>
              <DropdownMenuRoot>
                <DropdownMenuTrigger
                  class="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-rounded-md tw-bg-gray-200 tw-px-4 tw-py-2 tw-text-sm tw-outline-none hover:tw-bg-gray-400"
                >
                  Playback Speed ({{ wsPlaybackSpeed }}x)
                  <i aria-hidden="true" class="fas fa-chevron-right"></i>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  :side-offset="10"
                  class="tw-z-[100000] tw-flex tw-max-h-[--reka-popper-available-height] tw-w-full tw-max-w-[20rem] tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-500 tw-bg-gray-200 tw-p-2"
                >
                  <DropdownMenuRadioGroup
                    :model-value="wsPlaybackSpeed.toString()"
                    @update:model-value="(value) => setPlaybackSpeed(+value)"
                  >
                    <DropdownMenuRadioItem
                      v-for="speed in playbackSpeedSteps"
                      :key="speed"
                      :value="speed.toString()"
                      class="tw-grid tw-cursor-pointer tw-grid-cols-[1rem_minmax(0,1fr)] tw-items-center tw-justify-start tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 tw-outline-none hover:tw-bg-gray-400"
                    >
                      <DropdownMenuItemIndicator>
                        <i class="fas fa-check fa-2xs"></i>
                      </DropdownMenuItemIndicator>
                      <span class="tw-col-start-2 tw-text-sm">{{ speed }}x</span>
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenuRoot>
            </div>
          </div>
          <div class="[grid-area:content]">
            <div>
              <i class="fas fa-info-circle fa-sm"></i>
              Use your mouse to zoom and scroll the waveform
            </div>
            <ul class="tw-list tw-list-inside tw-list-disc">
              <li>
                Zoom:
                {{ calculatedZoom }}%
              </li>
              <li>
                Selected:
                <span class="tw-font-medium">
                  {{ toDuration(regionStart) }}
                </span>
                -
                <span class="tw-font-medium">
                  {{ toDuration(regionEnd) }}
                </span>
                (<span class="tw-font-semibold"> {{ toDurationHumanReadableText(regionEnd - regionStart) }} </span>)
              </li>
            </ul>
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <div class="tw-flex tw-items-center">
                <input id="simulate-redaction" v-model="simulateRedaction" type="checkbox" />
                <label for="simulate-redaction" class="tw-mx-2 tw-cursor-pointer">Simulate Redaction</label>
              </div>
              <div class="tw-flex-1"></div>
              <button
                class="tw-rounded-sm tw-bg-purple-branddark tw-px-2 tw-py-1 tw-text-white"
                :aria-pressed="isPlaying"
                :aria-label="isPlaying ? 'Pause Audio' : 'Play Audio'"
                @click="() => scrollToCenter(regionStart)"
              >
                Scroll to Region
              </button>
            </div>
            <div class="tw-py-2">
              <hr class="tw-h-px tw-border-none tw-bg-black/50" />
            </div>
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <PopoverRoot v-if="markMetadata.uuid != null">
                <PopoverTrigger
                  class="tw-rounded-md tw-px-3 tw-py-1 tw-text-red-800 hover:tw-bg-red-300/40 data-[state=open]:tw-bg-red-300/20"
                >
                  <i v-if="isDeleting" class="fas fa-spinner tw-animate-spin" />
                  Delete
                </PopoverTrigger>
                <PopoverPortal>
                  <PopoverContent
                    side="bottom"
                    align="start"
                    class="tw-z-[100000000000] tw-flex tw-select-none tw-flex-col tw-items-end tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-bg-white tw-p-2 tw-text-xs tw-shadow"
                  >
                    <p class="tw-max-w-xs">Are you sure you want to delete this audio redaction?</p>
                    <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                      <PopoverClose class="tw-btn-md-gray">Cancel</PopoverClose>
                      <button
                        type="button"
                        class="tw-rounded-md tw-px-2 tw-py-1 tw-text-red-800 hover:tw-bg-red-500/40"
                        @click="onDeleteAnnotation"
                      >
                        Yes, Delete
                      </button>
                    </div>
                    <PopoverArrow />
                  </PopoverContent>
                </PopoverPortal>
              </PopoverRoot>
              <button
                type="button"
                :disabled="validationMessage.length > 0"
                class="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-bg-blue-300/30 tw-px-3 tw-py-1 tw-text-blue-800 tw-ring-blue-800"
                @click="() => saveTimeIndexRedaction()"
              >
                <i v-if="isSaving" class="fas fa-spinner tw-animate-spin" />
                Save
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
